import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Challenge, EntityTypeName, Entity } from '@shapeable/types';
import { breakpoints, theme, ColorTokens } from '@shapeable/theme';
import { classNames, entityTypeNameFor, ginni } from '@shapeable/utils';
import { ContentTab, ContentTabs, ContentTabSpec, EntityGrid, SliceLayoutBannerTabs, SliceLayoutEntityIconPageHeader, TabsContainer, UiComponentName, useComponentProps, useEntityTypes, usePodcasts, usePosts, useVideos } from '@shapeable/ui';
import { useReflections } from '../../hooks/use-reflections';
const cls = classNames('challenge-layout');

// -------- Types -------->

// -------- Props -------->

export type ChallengeLayoutProps = Classable & HasChildren & { 
  entity?: Challenge;
  maxBodyWidth?: string;
};

export const ChallengeLayoutDefaultProps: Omit<ChallengeLayoutProps, 'entity'> = {
  maxBodyWidth: '1088px',
};

// -------- Child Component Props -------->

type ContainerProps = {
  children?: React.ReactNode;
  className?: string;
};

type BoundaryProps = {
  maxWidth?: string;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  
  `,
});

const BoundaryStyles = breakpoints({
  base: css`
    box-sizing: border-box;
    width: 100%;
    padding: 0 ${theme.UNIT(2)};
    ${({ maxWidth }: BoundaryProps ) => css`
      max-width: ${maxWidth || '100%'};
      margin: 0 auto;
    `}
  `,
});


const TabsStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(2)};
    margin-bottom: ${theme.UNIT(6)};
  `,
});

const TabStyles = breakpoints({
  base: css`
    .shp--content-tab__icon-label {
      white-space: nowrap;
    }
  `,
});


const GridStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(8)};
  `,
});





// -------- Components -------->

const My = {
  NAME: 'ChallengeLayout' as UiComponentName,

  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Tabs: styled(TabsContainer)<ContainerProps>`${TabsStyles}`,
      Tab: styled(ContentTab)`${TabStyles}`,

    Boundary: styled.div<BoundaryProps & HasChildren>`${BoundaryStyles}`,
    
    Grid: styled(EntityGrid)`${GridStyles}`,

  Header: SliceLayoutEntityIconPageHeader,
};

export const ChallengeLayout: Shapeable.FC<ChallengeLayoutProps> = (props) => {
  const componentProps = useComponentProps(My.NAME, ChallengeLayoutDefaultProps, props) as ChallengeLayoutProps;
  const { className, children, entity } = componentProps;

  const reflections = useReflections();
  const posts = usePosts();
  const videos = useVideos();
  const podcasts = usePodcasts();

  const allEntityTypes = useEntityTypes();

  const entityTypes = allEntityTypes.filter((type) => ['Reflection', 'Post', 'Video', 'Podcast'].includes(type.internalName))

  const [ activeEntityType, setActiveEntityType ] = React.useState<EntityTypeName | null>(null);

  // to save time, let's setup a local filter - we can move this to a URL based scheme later

  const byChallenge = (item: Entity & { challenges?: Challenge[] }) => {
    return (item.challenges || []).some(challenge => challenge.slug === entity?.slug);
  };
  
  const itemsByType = React.useMemo(() => {
    // filter items by the current challenge
    return {
      Reflection: reflections.filter(byChallenge),
      Post: posts.filter(byChallenge),
      Video: videos.filter(byChallenge),
      Podcast: podcasts.filter(byChallenge),
    };
  }, [entity?.slug]) as Partial<Record<EntityTypeName, Entity[]>>;

  const items: Entity[] = React.useMemo(() => {
    return [
      ...itemsByType.Reflection,
      ...itemsByType.Post,
      ...itemsByType.Video,
      ...itemsByType.Podcast,
    ];
  }, [itemsByType]);

  const activeItems = React.useMemo(() => {
    if (activeEntityType === null) {
      return items;
    }

    return itemsByType[activeEntityType] || [];
  }, [ activeEntityType, items, entity?.slug, itemsByType ]);

  // console.log('ChallengeLayout', {
  //   entity,
  //   itemsByType,
  //   activeEntityType,
  //   activeItems,
  // });
  
  return (
    <My.Container className={cls.name(className)}>
      <My.Header />
      <SliceLayoutBannerTabs />

      <My.Boundary maxWidth={componentProps.maxBodyWidth}>
      <My.Tabs>
        <My.Tab 
          onClick={() => setActiveEntityType(null)} 
          isActive={activeEntityType === null} 
          isLast={false} 
          label="All Types" 
          count={items.length} 
        />
        {
          entityTypes.map((type) => {
            const count = itemsByType?.[type.internalName as EntityTypeName]?.length || 0;

            if (count === 0) {
              return null; // don't show tabs for types with no items
            }

            return (
              <My.Tab 
                key={type.internalName} 
                wordWrap="nowrap"
                onClick={() => setActiveEntityType(type.internalName as EntityTypeName)} 
                isActive={activeEntityType === type.internalName} 
                isLast={false} 
                label={type.pluralLabel || type.name} 
                count={count} 
              />
            );
          }).filter(Boolean) // filter out nulls
        }
      </My.Tabs>

      <My.Grid 
        spacing={4}
        maxColumns={4}
        anyCardProps={{ showDescription: false }}
        items={activeItems} 
      />
      
      </My.Boundary>

    </My.Container>
  )
};

ChallengeLayout.cls = cls;